/**
 * Token Validation Utility
 * Checks token validity and expiration
 */

/**
 * Check if token exists in localStorage
 * @returns {boolean}
 */
export const hasToken = () => {
  return !!localStorage.getItem("token");
};

/**
 * Check if token is expired based on expires_at timestamp
 * @returns {boolean}
 */
export const isTokenExpired = () => {
  const expiresAt = localStorage.getItem("token_expires_at");

  if (!expiresAt) {
    // If no expiration stored, assume token is valid
    // This handles legacy tokens or tokens stored before expiration tracking
    return false;
  }

  try {
    const expiryDate = new Date(expiresAt);
    const now = new Date();

    return expiryDate <= now;
  } catch (error) {
    console.error("Error parsing token expiration date:", error);
    // If we can't parse the date, assume not expired
    return false;
  }
};

/**
 * Check if token is valid (exists and not expired)
 * @returns {boolean}
 */
export const isTokenValid = () => {
  if (!hasToken()) {
    return false;
  }

  if (isTokenExpired()) {
    return false;
  }

  return true;
};

/**
 * Get token from localStorage
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem("token");
};

/**
 * Get token expiration info
 * @returns {object|null}
 */
export const getTokenExpiration = () => {
  const expiresAt = localStorage.getItem("token_expires_at");
  const expiresIn = localStorage.getItem("token_expires_in");

  if (!expiresAt) {
    return null;
  }

  try {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const secondsRemaining = Math.floor((expiryDate - now) / 1000);

    return {
      expiresAt: expiryDate,
      expiresIn: parseInt(expiresIn) || null,
      secondsRemaining: Math.max(0, secondsRemaining),
      isExpired: secondsRemaining <= 0,
      expiresInMinutes: Math.floor(secondsRemaining / 60),
      expiresInHours: Math.floor(secondsRemaining / 3600),
      expiresInDays: Math.floor(secondsRemaining / 86400),
    };
  } catch (error) {
    console.error("Error calculating token expiration:", error);
    return null;
  }
};

/**
 * Check if token is expiring soon (within specified minutes)
 * @param {number} minutes - Minutes threshold (default: 5)
 * @returns {boolean}
 */
export const isTokenExpiringSoon = (minutes = 5) => {
  const expiration = getTokenExpiration();

  if (!expiration) {
    return false;
  }

  const thresholdSeconds = minutes * 60;
  return (
    expiration.secondsRemaining <= thresholdSeconds &&
    expiration.secondsRemaining > 0
  );
};

/**
 * Clear all token data from localStorage
 */
export const clearTokenData = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("token_expires_at");
  localStorage.removeItem("token_expires_in");
};

/**
 * Validate and clean up expired tokens
 * Call this on app initialization
 * @returns {boolean} - True if token is valid, false if expired and cleared
 */
export const validateAndCleanup = () => {
  if (!hasToken()) {
    return false;
  }

  if (isTokenExpired()) {
    console.warn("⚠️  Token expired, clearing localStorage");
    clearTokenData();
    return false;
  }

  return true;
};

// Make available in browser console for debugging
if (typeof window !== "undefined") {
  window.tokenValidator = {
    isValid: isTokenValid,
    isExpired: isTokenExpired,
    getExpiration: getTokenExpiration,
    isExpiringSoon: isTokenExpiringSoon,
    clear: clearTokenData,
  };
}

export default {
  hasToken,
  isTokenExpired,
  isTokenValid,
  getToken,
  getTokenExpiration,
  isTokenExpiringSoon,
  clearTokenData,
  validateAndCleanup,
};
